import { useState, useEffect, useMemo } from 'react';
import type { CallBackProps, Step } from 'react-joyride';
import { STATUS } from 'react-joyride';
import {
  checkAndMarkOnboardingComplete,
  markTourCompleteOnServer,
  waitForOnboardingSync,
} from '../../../../../utils/onboarding';

/**
 * usePatientsTour encapsulates all react-joyride state and callbacks for the
 * Pacientes onboarding tour, keeping PatientsContent.tsx focused on layout.
 *
 * A diferencia de Sesiones (useSessionsTour), acá no hace falta un MutationObserver
 * de pausa/resume entre pasos: el paciente ficticio (ver buildTourPatients) queda
 * visible durante todo el tour, no cambia por paso, así que los 4 targets ya existen
 * en el DOM apenas arranca.
 */

// Module-level flag so the tooltip renderer can access the skip state without
// prop-drilling through the Joyride tooltipComponent callback.
export let patientsCanSkip = false;

export type UsePatientsTourOptions = {
  /** true una vez que el fetch inicial de pacientes terminó (loading === false). */
  patientsLoaded: boolean;
  /** El tour solo corre en la tab "Todos" — ahí vive el paciente ficticio. */
  isOnAllTab: boolean;
};

export type UsePatientsTourResult = {
  runTour: boolean;
  tourKey: number;
  tourStepIndex: number;
  steps: Step[];
  canSkip: boolean;
  onTourCallback: (data: CallBackProps) => void;
  onReplayTour: () => void;
};

export function usePatientsTour({ patientsLoaded, isOnAllTab }: UsePatientsTourOptions): UsePatientsTourResult {
  const [runTour, setRunTour] = useState<boolean>(false);
  const [tourKey, setTourKey] = useState<number>(0);
  const [tourStepIndex, setTourStepIndex] = useState<number>(-1);
  const [canSkip, setCanSkip] = useState(false);

  const steps: Step[] = useMemo(
    () => [
      {
        target: '#patients-tour-tabs',
        content: 'Estos son los estados de tus pacientes: Nuevos, Activos, En pausa, Inactivos y Derivación exitosa. Cada uno agrupa a tus pacientes según en qué etapa del proceso están.',
        disableBeacon: true,
      },
      {
        target: '#patients-tour-search',
        content: 'Buscá un paciente puntual por nombre o email desde acá.',
        disableBeacon: true,
      },
      {
        target: '#patient-row-tour-patient',
        content: 'Así se ve un paciente en la tabla: podés ver su estado, estado de pago, próxima sesión y cantidad de sesiones totales.',
        disableBeacon: true,
      },
      {
        target: '#patient-tour-actions-cell',
        content: 'Desde acá manejás todo lo relacionado a este paciente: "Ver perfil" te lleva a su historia clínica completa, y el menú "..." te da más opciones — agregar una nota a su historia clínica, completar el formulario de no agendamiento, iniciar una derivación, o marcarlo como urgente si necesita atención prioritaria.',
        disableBeacon: true,
      },
    ],
    [],
  );

  useEffect(() => {
    if (!patientsLoaded || !isOnAllTab) return;
    let cancelled = false;
    waitForOnboardingSync().then(() => {
      if (cancelled) return;
      try {
        const done = localStorage.getItem('psi-patients-tour-done');
        if (!done) {
          patientsCanSkip = false;
          setCanSkip(false);
          setTimeout(() => {
            if (!cancelled) {
              setTourStepIndex(0);
              setRunTour(true);
            }
          }, 300);
        } else {
          patientsCanSkip = true;
          setCanSkip(true);
        }
      } catch {
        /* noop */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [patientsLoaded, isOnAllTab]);

  const onTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & { action?: string };
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      setTourStepIndex(-1);
      const wasAlreadyDone = localStorage.getItem('psi-patients-tour-done') === '1';
      try {
        localStorage.setItem('psi-patients-tour-done', '1');
      } catch {
        /* noop */
      }
      checkAndMarkOnboardingComplete();
      if (!wasAlreadyDone) markTourCompleteOnServer('psi-patients-tour-done');
      return;
    }
    if (type === 'step:after') {
      if (action === 'next') setTourStepIndex((index ?? 0) + 1);
      if (action === 'prev') setTourStepIndex(Math.max(0, (index ?? 0) - 1));
    }
  };

  const onReplayTour = () => {
    setTourKey((k) => k + 1);
    setTourStepIndex(0);
    setRunTour(true);
  };

  return { runTour, tourKey, tourStepIndex, steps, canSkip, onTourCallback, onReplayTour };
}
